import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, ShieldCheck, Sparkles, Users, TrendingUp, MapPin, Briefcase, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1573164574511-73c773193279?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBqb2IlMjBpbnRlcnZpZXclMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3ODg2NzUzODV8MA&ixlib=rb-4.1.0&q=85";

const stats = [
  { label: "Active Openings", value: "12K+", Icon: Briefcase },
  { label: "Talent Placed", value: "38K+", Icon: Users },
  { label: "Partner Companies", value: "1.2K", Icon: TrendingUp },
  { label: "Avg. Match Rating", value: "4.8/5", Icon: Star },
];

const perks = [
  { title: "Verified Employers", desc: "Every listing is screened for legitimacy and salary transparency.", Icon: ShieldCheck },
  { title: "Personalised Matches", desc: "Our team hand-picks roles based on your experience and goals.", Icon: Sparkles },
  { title: "Career Support", desc: "Resume review, mock interviews and negotiation guidance.", Icon: Users },
];

const roles = ["Engineering", "Design", "Marketing", "Sales", "Analytics", "Human Resources", "Operations", "Finance"];

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-70" />
        <div className="blob bg-indigo-500 w-[500px] h-[500px] -top-40 -left-32" />
        <div className="blob bg-sky-400 w-[420px] h-[420px] -bottom-40 -right-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 backdrop-blur-md bg-white/80 border border-slate-200/70 shadow-sm rounded-full py-1.5 px-4 text-xs font-semibold tracking-[0.14em] uppercase text-indigo-700"
              >
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Hiring across 20+ industries
              </motion.span>

              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900"
              >
                Find the Right Job.
                <br />
                <span className="text-gradient-blue">Build Your Future.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed"
              >
                Submit your job enquiry and let us help you find opportunities that match your skills and experience — curated by real career mentors, not algorithms alone.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mt-8 flex flex-col sm:flex-row gap-3"
              >
                <Link to="/enquiry">
                  <Button data-testid="hero-submit-enquiry" size="lg" className="rounded-full px-7 h-12 bg-gradient-to-r from-blue-700 to-indigo-600 hover:from-blue-800 hover:to-indigo-700 text-white font-semibold shadow-lg shadow-indigo-500/25">
                    Submit Job Enquiry
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/jobs">
                  <Button data-testid="hero-browse-jobs" size="lg" variant="outline" className="rounded-full px-7 h-12 border-slate-300 hover:border-indigo-500 hover:bg-indigo-50 text-slate-800 font-semibold">
                    <Search className="mr-2 w-4 h-4" />
                    Browse Jobs
                  </Button>
                </Link>
              </motion.div>

              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {stats.map(({ label, value, Icon }) => (
                  <div key={label} className="bg-white/70 backdrop-blur border border-slate-200 rounded-2xl p-4">
                    <Icon size={16} className="text-indigo-600" />
                    <p className="mt-2 text-2xl font-extrabold text-slate-900 font-display">{value}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="relative"
              >
                <div className="absolute -inset-4 bg-gradient-to-br from-blue-500/20 to-sky-500/20 rounded-3xl blur-2xl" />
                <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-indigo-500/20 border border-white/60">
                  <img src={HERO_IMG} alt="Team collaboration in modern office" className="w-full h-[460px] object-cover" />
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                  className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 animate-float-slow"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center">
                      <ShieldCheck size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Verified matches</p>
                      <p className="font-bold text-slate-900 font-display">98% success rate</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.85 }}
                  className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 border border-slate-100"
                >
                  <p className="text-xs text-slate-500">Applications this week</p>
                  <p className="mt-1 font-display font-extrabold text-2xl text-slate-900">2,847<span className="ml-1 text-emerald-600 text-sm font-semibold">↑ 12%</span></p>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories strip */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 overflow-x-auto no-scrollbar">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 whitespace-nowrap">Explore roles</span>
            {roles.map((r) => (
              <Link key={r} to={`/jobs?category=${r}`} className="whitespace-nowrap text-sm px-4 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-indigo-500 hover:text-indigo-700 transition">
                {r}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Why CareerConnect</p>
          <h2 className="mt-4 text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 font-display">
            Recruitment that actually respects your time.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            We combine expert human curation with a lightweight process, so every conversation moves your career forward — no ghosting, no spam listings.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {perks.map(({ title, desc, Icon }) => (
            <div key={title} className="card-hover bg-white border border-slate-200 rounded-2xl p-6">
              <span className="inline-flex w-11 h-11 rounded-xl bg-indigo-50 text-indigo-700 items-center justify-center">
                <Icon size={20} />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900 font-display">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="relative overflow-hidden rounded-3xl p-10 lg:p-14 bg-gradient-to-br from-blue-800 via-indigo-700 to-sky-600 text-white">
          <div className="absolute inset-0 opacity-30 grid-pattern" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h3 className="text-3xl lg:text-4xl font-extrabold tracking-tight font-display">Ready to take the next step in your career?</h3>
              <p className="mt-3 text-white/85 max-w-2xl">Share your details in a single form and a mentor will reach out within 48 hours with curated opportunities.</p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end gap-3">
              <Link to="/enquiry">
                <Button data-testid="cta-submit-enquiry" size="lg" className="rounded-full px-7 h-12 bg-white text-indigo-800 hover:bg-slate-100 font-semibold">
                  Submit Enquiry <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
